/**
 * Step2SegmentsCard
 * Step 2 of the plan workflow - Generate and select campaign segments
 * Requires PLAN_FOCUS and PRIMARY_OFFER selection before generation
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Target, Check, Loader2, RefreshCw, ChevronDown, ChevronUp, Users, Sparkles, AlertTriangle, Star, FileText, Zap, Settings2, Plus, X } from 'lucide-react';
import { plansApi } from '../api/plansApi';

const STAGE_COLORS = {
  awareness: 'bg-blue-500/10 text-blue-600 border-blue-200',
  consideration: 'bg-purple-500/10 text-purple-600 border-purple-200',
  decision: 'bg-green-500/10 text-green-600 border-green-200',
  retention: 'bg-teal-500/10 text-teal-600 border-teal-200',
  retargeting: 'bg-orange-500/10 text-orange-600 border-orange-200',
  returning_high_intent: 'bg-orange-500/10 text-orange-600 border-orange-200'
};

const RECOMMENDATION_COLORS = {
  high: 'bg-green-500/10 text-green-700 border-green-300',
  medium: 'bg-amber-500/10 text-amber-700 border-amber-300',
  low: 'bg-gray-500/10 text-gray-600 border-gray-300'
};

// Plan Focus options for segment generation
const PLAN_FOCUS_OPTIONS = [
  { value: 'acquire', label: 'Acquire', description: 'Cold, first-time visitors (awareness/early consideration)' },
  { value: 'convert', label: 'Convert', description: 'High-intent, decision-stage users ready to act' },
  { value: 'retarget', label: 'Retarget', description: 'Returning visitors who engaged but didn\'t convert' },
  { value: 'retain', label: 'Retain', description: 'Existing customers (renewal, upsell, cross-sell)' }
];

// Segment Item Component
function SegmentItem({ segment, isSelected, isExpanded, isSuggested, onToggleSelect, onToggleExpand }) {
  const recLevel = segment.recommendation_level || 'medium';
  
  return (
    <div className={`border rounded-lg transition-all ${isSelected ? 'border-purple-400 bg-purple-500/5' : isSuggested ? 'border-green-300 bg-green-500/5' : 'border-border hover:border-purple-200'}`}>
      <div className="p-3 cursor-pointer" onClick={onToggleSelect}>
        <div className="flex items-start gap-3">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${isSelected ? 'bg-purple-500 border-purple-500' : 'border-muted-foreground/30'}`}>
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm">{segment.name}</span>
              {isSuggested && (
                <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-300">
                  <Star className="w-3 h-3 mr-1" />
                  Suggested
                </Badge>
              )}
              <Badge variant="outline" className={`text-xs ${STAGE_COLORS[segment.definition?.stage] || ''}`}>
                {segment.definition?.stage?.replace('_', ' ')}
              </Badge>
              <Badge variant="outline" className={`text-xs ${RECOMMENDATION_COLORS[recLevel]}`}>
                {recLevel}
              </Badge>
            </div>
            
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {segment.why_this_segment}
            </p>
            
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span>{segment.recommendation_reason}</span>
            </div>
          </div>
          
          <Button variant="ghost" size="sm" className="shrink-0" onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}>
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t border-border mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3">
            {/* Definition */}
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground uppercase">Segment Definition</h5>
              <div className="text-sm space-y-1">
                <div><span className="text-muted-foreground">Intent:</span> {segment.definition?.intent}</div>
                <div><span className="text-muted-foreground">Products:</span> {segment.definition?.product_interest?.join(', ')}</div>
                {segment.definition?.situational_traits?.length > 0 && (
                  <div><span className="text-muted-foreground">Traits:</span> {segment.definition?.situational_traits?.join(', ')}</div>
                )}
              </div>
            </div>
            
            {/* Page Requirement */}
            <div className="space-y-2">
              <h5 className="text-xs font-medium text-muted-foreground uppercase">Page Requirement</h5>
              <div className="text-sm">
                <Badge variant="outline" className={segment.page_requirement?.type === 'personalize_existing' ? 'bg-green-500/10 text-green-600' : 'bg-orange-500/10 text-orange-600'}>
                  <FileText className="w-3 h-3 mr-1" />
                  {segment.page_requirement?.type === 'personalize_existing' ? 'Personalize Existing' : 'New Page Needed'}
                </Badge>
                {segment.page_requirement?.existing_target_refs?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Target: {segment.page_requirement.existing_target_refs.join(', ')}
                  </p>
                )}
                {segment.page_requirement?.new_page?.needed && segment.page_requirement?.new_page?.page_name && (
                  <p className="text-xs text-muted-foreground mt-1">
                    New page: {segment.page_requirement.new_page.page_name}
                  </p>
                )}
              </div>
            </div>
            
            {/* Evidence */}
            {segment.evidence?.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-xs font-medium text-muted-foreground uppercase">Evidence</h5>
                <div className="space-y-1">
                  {segment.evidence.slice(0, 3).map((ev, idx) => (
                    <div key={idx} className="text-xs">
                      <span className="text-muted-foreground">[{ev.source}]</span> {ev.detail}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Dependencies */}
            {segment.dependencies?.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-xs font-medium text-muted-foreground uppercase">Dependencies</h5>
                {segment.dependencies.slice(0, 3).map((dep, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <AlertTriangle className={`w-3 h-3 shrink-0 mt-0.5 ${dep.severity === 'high' ? 'text-red-500' : dep.severity === 'med' ? 'text-orange-500' : 'text-yellow-500'}`} />
                    <span>{dep.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Step2SegmentsCard({ planId, stepData, isCompleted, onSave, onEdit, saving }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [segments, setSegments] = useState(stepData?.generated_segments || []);
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [selectedIds, setSelectedIds] = useState(stepData?.selected_segment_ids || []);
  const [expandedSegments, setExpandedSegments] = useState(new Set());
  const [recommendedSelection, setRecommendedSelection] = useState(null);
  
  // New state for Plan Focus and Primary Offer
  const [planFocus, setPlanFocus] = useState(stepData?.plan_focus || '');
  const [primaryOffer, setPrimaryOffer] = useState(stepData?.primary_offer || '');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [needsSelection, setNeedsSelection] = useState(true);
  
  // State for Add More Segments feature
  const [showAddSegment, setShowAddSegment] = useState(false);
  const [customSegmentHint, setCustomSegmentHint] = useState('');
  const [addingSegment, setAddingSegment] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState(null);

  useEffect(() => {
    // Update segments from stepData if available
    if (stepData?.generated_segments?.length > 0) {
      setSegments(stepData.generated_segments);
    }
    if (stepData?.selected_segment_ids) {
      setSelectedIds(stepData.selected_segment_ids);
    }
  }, [stepData]);

  useEffect(() => {
    if (!isCompleted) {
      loadInitialData();
    } else {
      setLoading(false);
    }
  }, [planId, isCompleted]);

  // Load initial data to get available products and any saved state
  const loadInitialData = async () => {
    setLoading(true);
    try {
      const data = await plansApi.getSegments(planId, false);
      
      if (data.error) {
        toast.error(data.error);
        return;
      }
      
      // Set available products for dropdown
      if (data.available_products) {
        setAvailableProducts(data.available_products);
      }
      
      // Load saved selections if available
      if (data.plan_focus) setPlanFocus(data.plan_focus);
      if (data.primary_offer) setPrimaryOffer(data.primary_offer);
      if (data.segments?.length > 0) {
        setSegments(data.segments);
        setNeedsSelection(false);
      } else {
        setNeedsSelection(data.needs_selection !== false);
      }
      
      setSelectedPersonas(data.selected_personas || []);
      setRecommendedSelection(data.recommended_selection || null);
      
      // Auto-select suggested picks if no selections yet
      if (data.recommended_selection?.recommended_segments && selectedIds.length === 0) {
        setSelectedIds(data.recommended_selection.recommended_segments);
      }
      
    } catch (error) {
      console.error('Failed to load segment data:', error);
      toast.error('Failed to load segment data');
    } finally {
      setLoading(false);
    }
  };

  // Generate segments with selected Plan Focus and Primary Offer
  const generateSegments = async () => {
    if (!planFocus) {
      toast.error('Please select a Plan Focus');
      return;
    }
    if (!primaryOffer) {
      toast.error('Please select a Primary Offer');
      return;
    }
    
    setGenerating(true);
    try {
      const data = await plansApi.getSegments(planId, true, planFocus, primaryOffer);
      
      if (data.error) {
        toast.error(data.error);
        return;
      }
      
      setSegments(data.segments || []);
      setSelectedPersonas(data.selected_personas || []);
      setRecommendedSelection(data.recommended_selection || null);
      setNeedsSelection(false);
      
      // Auto-select suggested picks
      if (data.recommended_selection?.recommended_segments) {
        setSelectedIds(data.recommended_selection.recommended_segments);
      }
      
      toast.success('Segments generated successfully');
    } catch (error) {
      console.error('Failed to generate segments:', error);
      toast.error('Failed to generate segments');
    } finally {
      setGenerating(false);
    }
  };

  // Regenerate with different focus/offer
  const handleRegenerate = () => {
    setNeedsSelection(true);
    setSegments([]);
  };

  // Handle adding a custom segment
  const handleAddCustomSegment = async () => {
    if (!customSegmentHint.trim()) {
      toast.error('Please describe the segment you want to add');
      return;
    }
    
    setAddingSegment(true);
    setOverlapWarning(null);
    
    try {
      const result = await plansApi.addCustomSegment(planId, customSegmentHint);
      
      if (result.status === 'ok') {
        // Successfully added
        setSegments(prev => [...prev, result.segment]);
        setSelectedIds(prev => [...prev, result.segment.segment_id]);
        setCustomSegmentHint('');
        setShowAddSegment(false);
        toast.success('Segment added successfully');
        
        if (result.overlap?.status === 'possible') {
          toast.info('Note: This segment has some overlap with existing segments');
        }
      } else if (result.status === 'overlap') {
        // High overlap - ask user to confirm
        setOverlapWarning({
          segment: result.segment,
          overlap: result.overlap,
          message: result.message
        });
      } else if (result.status === 'invalid') {
        // Invalid - show error with suggestion
        toast.error(result.message);
        if (result.suggestion) {
          toast.info(`Suggestion: ${result.suggestion}`, { duration: 5000 });
        }
      } else {
        toast.error(result.message || 'Failed to add segment');
      }
    } catch (error) {
      console.error('Failed to add custom segment:', error);
      toast.error('Failed to add segment. Please try again.');
    } finally {
      setAddingSegment(false);
    }
  };

  // Force add a segment despite overlap warning
  const handleForceAddSegment = async () => {
    if (!overlapWarning?.segment) return;
    
    setAddingSegment(true);
    try {
      const result = await plansApi.addSegmentForce(planId, overlapWarning.segment);
      
      if (result.status === 'ok') {
        setSegments(prev => [...prev, result.segment]);
        setSelectedIds(prev => [...prev, result.segment.segment_id]);
        setCustomSegmentHint('');
        setShowAddSegment(false);
        setOverlapWarning(null);
        toast.success('Segment added (recommendation downgraded due to overlap)');
      } else {
        toast.error('Failed to add segment');
      }
    } catch (error) {
      console.error('Failed to force-add segment:', error);
      toast.error('Failed to add segment');
    } finally {
      setAddingSegment(false);
    }
  };

  const cancelAddSegment = () => {
    setShowAddSegment(false);
    setCustomSegmentHint('');
    setOverlapWarning(null);
  };

  const toggleSelection = (segmentId) => {
    setSelectedIds(prev => prev.includes(segmentId) ? prev.filter(id => id !== segmentId) : [...prev, segmentId]);
  };

  const toggleExpand = (segmentId) => {
    setExpandedSegments(prev => {
      const newSet = new Set(prev);
      newSet.has(segmentId) ? newSet.delete(segmentId) : newSet.add(segmentId);
      return newSet;
    });
  };

  const handleSave = () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one segment');
      return;
    }
    onSave({ generated_segments: segments, selected_segment_ids: selectedIds });
  };

  const getPersonaName = (personaId) => {
    const persona = selectedPersonas.find(p => p.persona_id === personaId || p.id === personaId);
    return persona?.name || 'Unknown Persona';
  };

  // Group segments by persona
  const segmentsByPersona = segments.reduce((acc, seg) => {
    const pid = seg.persona_id;
    if (!acc[pid]) acc[pid] = [];
    acc[pid].push(seg);
    return acc;
  }, {});

  // Get suggested segment IDs
  const suggestedIds = new Set(recommendedSelection?.suggested_picks || []);

  if (loading) {
    return (
      <div className="border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Generating segments...</span>
        </div>
      </div>
    );
  }

  if (isCompleted && !loading) {
    const selectedSegments = segments.filter(s => selectedIds.includes(s.segment_id));
    
    // Group selected segments by persona for display
    const selectedByPersona = selectedSegments.reduce((acc, seg) => {
      const pid = seg.persona_id;
      if (!acc[pid]) acc[pid] = [];
      acc[pid].push(seg);
      return acc;
    }, {});
    
    return (
      <div className="border border-green-200 dark:border-green-800 bg-green-500/5 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-500" />
            <h3 className="font-semibold text-sm">Step 2: Segments</h3>
            <Badge variant="outline" className="bg-green-500/10 text-green-600 text-xs">{selectedIds.length} selected</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-8 px-2">
            <Settings2 className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
        </div>
        
        {/* Show segments grouped by persona */}
        <div className="space-y-3">
          {Object.entries(selectedByPersona).map(([personaId, personaSegments]) => (
            <div key={personaId} className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="w-3 h-3" />
                <span className="font-medium">{getPersonaName(personaId)}</span>
              </div>
              <div className="space-y-1 pl-4">
                {personaSegments.slice(0, 3).map(seg => (
                  <div key={seg.segment_id} className="flex items-center gap-1.5 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      seg.definition?.stage === 'decision' ? 'bg-green-500' :
                      seg.definition?.stage === 'consideration' ? 'bg-purple-500' :
                      seg.definition?.stage === 'retargeting' ? 'bg-orange-500' :
                      seg.definition?.stage === 'awareness' ? 'bg-blue-500' :
                      'bg-slate-400'
                    }`}></span>
                    <span className="text-slate-700 dark:text-slate-300 truncate">{seg.name}</span>
                  </div>
                ))}
                {personaSegments.length > 3 && (
                  <div className="text-xs text-muted-foreground pl-3">+{personaSegments.length - 3} more</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-purple-500" />
          <h3 className="font-semibold">Step 2: Campaign Segments</h3>
          {segments.length > 0 && <Badge variant="outline">{segments.length} generated</Badge>}
        </div>
        {segments.length > 0 && (
          <Button variant="ghost" size="sm" onClick={handleRegenerate}>
            <Settings2 className="w-4 h-4 mr-1" />
            Change Focus
          </Button>
        )}
      </div>

      {/* Plan Focus & Primary Offer Selection */}
      {(needsSelection || segments.length === 0) && (
        <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            <Zap className="w-4 h-4 text-purple-500" />
            Configure Segment Generation
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Plan Focus Dropdown */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                Plan Focus (Motion)
              </label>
              <Select value={planFocus} onValueChange={setPlanFocus}>
                <SelectTrigger className="w-full" data-testid="plan-focus-select">
                  <SelectValue placeholder="Select marketing motion..." />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_FOCUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{opt.label}</span>
                        <span className="text-xs text-muted-foreground">{opt.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {planFocus && (
                <p className="text-xs text-muted-foreground">
                  {PLAN_FOCUS_OPTIONS.find(o => o.value === planFocus)?.description}
                </p>
              )}
            </div>
            
            {/* Primary Offer Dropdown */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                Primary Offer (Product)
              </label>
              <Select value={primaryOffer} onValueChange={setPrimaryOffer}>
                <SelectTrigger className="w-full" data-testid="primary-offer-select">
                  <SelectValue placeholder="Select product to focus on..." />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.length > 0 ? (
                    availableProducts.map(product => (
                      <SelectItem key={product} value={product}>
                        {product}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="__default__" disabled>
                      No products found - check asset setup
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {primaryOffer && (
                <p className="text-xs text-muted-foreground">
                  All segments will be anchored to: <span className="font-medium">{primaryOffer}</span>
                </p>
              )}
            </div>
          </div>
          
          <div className="pt-2">
            <Button 
              onClick={generateSegments} 
              disabled={generating || !planFocus || !primaryOffer}
              className="bg-purple-500 hover:bg-purple-600"
              data-testid="generate-segments-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Segments...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Segments
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Show selected focus/offer when segments exist */}
      {segments.length > 0 && !needsSelection && (
        <div className="bg-purple-500/5 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
          <div className="flex items-center gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Focus:</span>
              <Badge variant="outline" className="ml-2 bg-purple-500/10 text-purple-700">
                {PLAN_FOCUS_OPTIONS.find(o => o.value === planFocus)?.label || planFocus}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Product:</span>
              <Badge variant="outline" className="ml-2 bg-blue-500/10 text-blue-700">
                {primaryOffer}
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation note */}
      {recommendedSelection?.note && segments.length > 0 && (
        <div className="bg-green-500/5 border border-green-200 dark:border-green-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-green-500 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-green-700">Recommendation:</span>
              <span className="ml-1 text-muted-foreground">{recommendedSelection.note}</span>
              {recommendedSelection.recommended_segments?.length > 0 && (
                <span className="ml-1 text-green-600">
                  ({recommendedSelection.recommended_segments.length} pre-selected)
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedPersonas.length > 0 && segments.length > 0 && (
        <div className="bg-purple-500/5 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Users className="w-4 h-4 text-purple-500 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium">Segments for:</span>
              <span className="ml-1 text-muted-foreground">{selectedPersonas.map(p => p.name).join(', ')}</span>
            </div>
          </div>
        </div>
      )}

      {Object.entries(segmentsByPersona).map(([personaId, personaSegments]) => (
        <div key={personaId} className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <Users className="w-4 h-4" />
            {getPersonaName(personaId)}
            <Badge variant="outline" className="text-xs">{personaSegments.length} segments</Badge>
          </h4>
          <div className="space-y-2">
            {personaSegments.map(segment => (
              <SegmentItem
                key={segment.segment_id}
                segment={segment}
                isSelected={selectedIds.includes(segment.segment_id)}
                isExpanded={expandedSegments.has(segment.segment_id)}
                isSuggested={suggestedIds.has(segment.segment_id)}
                onToggleSelect={() => toggleSelection(segment.segment_id)}
                onToggleExpand={() => toggleExpand(segment.segment_id)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Add More Segments Section */}
      {segments.length > 0 && !needsSelection && (
        <div className="space-y-3">
          {!showAddSegment ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddSegment(true)}
              className="w-full border-dashed hover:border-purple-400 hover:bg-purple-500/5"
              data-testid="add-more-segments-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add More Segments
            </Button>
          ) : (
            <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-4 space-y-4 bg-purple-500/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Plus className="w-4 h-4 text-purple-500" />
                  Add Custom Segment
                </div>
                <Button variant="ghost" size="sm" onClick={cancelAddSegment}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">
                  Describe the segment you want to add (intent, situation, or objection):
                </label>
                <Textarea
                  value={customSegmentHint}
                  onChange={(e) => setCustomSegmentHint(e.target.value)}
                  placeholder="e.g., 'Users who are concerned about data privacy', 'People comparing us to competitors', 'First-time filers who are anxious about mistakes'..."
                  className="min-h-[80px] resize-none"
                  data-testid="custom-segment-input"
                  disabled={addingSegment}
                />
                <p className="text-xs text-muted-foreground">
                  Focus: <span className="font-medium">{PLAN_FOCUS_OPTIONS.find(o => o.value === planFocus)?.label}</span> | 
                  Product: <span className="font-medium">{primaryOffer}</span>
                </p>
              </div>
              
              {/* Overlap Warning */}
              {overlapWarning && (
                <div className="bg-amber-500/10 border border-amber-300 rounded-lg p-3 space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <span className="font-medium text-amber-700">Overlap Detected:</span>
                      <span className="ml-1 text-amber-600">{overlapWarning.message}</span>
                    </div>
                  </div>
                  {overlapWarning.segment && (
                    <div className="text-xs text-muted-foreground bg-white/50 dark:bg-black/20 rounded p-2">
                      <span className="font-medium">Suggested segment:</span> {overlapWarning.segment.name}
                    </div>
                  )}
                  {overlapWarning.overlap?.notes && (
                    <div className="text-xs text-muted-foreground">
                      {overlapWarning.overlap.notes}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOverlapWarning(null)}
                      disabled={addingSegment}
                    >
                      Modify Input
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleForceAddSegment}
                      disabled={addingSegment}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      {addingSegment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      Add Anyway (Lower Priority)
                    </Button>
                  </div>
                </div>
              )}
              
              {!overlapWarning && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelAddSegment}
                    disabled={addingSegment}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddCustomSegment}
                    disabled={addingSegment || !customSegmentHint.trim()}
                    className="bg-purple-500 hover:bg-purple-600"
                    data-testid="submit-custom-segment-btn"
                  >
                    {addingSegment ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Validating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Segment
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {segments.length === 0 && !needsSelection && (
        <div className="text-center py-8 text-muted-foreground">
          <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No segments generated yet.</p>
          <p className="text-sm">Select Plan Focus and Primary Offer above to generate.</p>
        </div>
      )}

      {segments.length > 0 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">{selectedIds.length} of {segments.length} selected</div>
          <Button onClick={handleSave} disabled={saving || selectedIds.length === 0} className="bg-purple-500 hover:bg-purple-600">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Confirm Segments
          </Button>
        </div>
      )}
    </div>
  );
}
